import { useCallback, useEffect, useState } from 'react';
import { AppLayout } from '../../components/layout/AppLayout';
import { Badge } from '../../components/ui/Badge';
import { Button, IconButton } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { ActionMenu } from '../../components/ui/ActionMenu';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Metric, Spinner, StateBlock } from '../../components/ui/Feedback';
import { useToast } from '../../components/ui/ToastProvider';
import { EditIcon, FilterIcon, PlusIcon, TrashIcon } from '../../components/icons/Icons';
import { api, errorMessage } from '../../lib/api';
import { useAsync, useDebounced } from '../../lib/hooks';
import { VENDOR_TYPES, type Paginated, type Vendor, type VendorStats, type VendorType } from '../../types/api';
import { VENDOR_TYPE_META } from '../../lib/status';
import { VendorFormDialog } from './components/VendorFormDialog';

const PER_PAGE_OPTIONS = [10, 25, 50].map((value) => ({ value, label: String(value) }));

const TYPE_OPTIONS = [
  { value: 'all', label: 'All vendor types' },
  ...VENDOR_TYPES.map((value) => ({ value, label: VENDOR_TYPE_META[value].label })),
];

/**
 * "Vendor Master" - the second entry in the rail.
 *
 * The Figma file has no dedicated frame for this screen, so it reuses the
 * "Vendors & Status" table from the review step (Vendor Name, Vendor Type,
 * Contact Person, Contact Number, Email) plus the same toolbar, badge and
 * pager components as the RFP list.
 */
export function VendorMasterPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<VendorType | 'all'>('all');
  const [dialogKey, setDialogKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [deleting, setDeleting] = useState(false);

  const debouncedSearch = useDebounced(search, 300);
  const { showToast } = useToast();

  const loader = useCallback(
    () =>
      api.get<Paginated<Vendor>>('/vendors', {
        page,
        perPage,
        search: debouncedSearch || undefined,
        vendorType: typeFilter === 'all' ? undefined : typeFilter,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    [page, perPage, debouncedSearch, typeFilter],
  );

  const { data, loading, error, reload } = useAsync(loader, [
    page,
    perPage,
    debouncedSearch,
    typeFilter,
  ]);

  const statsLoader = useCallback(() => api.get<VendorStats>('/vendors/stats'), []);
  const { data: stats, reload: reloadStats } = useAsync(statsLoader, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, perPage]);

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  const openDialog = (vendor: Vendor | null) => {
    setEditing(vendor);
    setDialogKey((value) => value + 1);
    setDialogOpen(true);
  };

  const refresh = () => {
    reload();
    reloadStats();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/vendors/${deleteTarget.id}`);
      showToast(`${deleteTarget.name} was removed from the vendor master.`);
      setDeleteTarget(null);
      refresh();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AppLayout title="RFP">
      <div className="page-header">
        <div className="toolbar">
          <span className="toolbar__title">
            All Vendors ({stats?.total ?? pagination?.total ?? 0})
          </span>
          <SearchInput
            placeholder="Search..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search vendors"
          />
          <Select
            aria-label="Filter by vendor type"
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value as VendorType | 'all')}
            wrapClassName="toolbar__select"
          />
          <IconButton label="More filters" brand>
            <FilterIcon size={18} />
          </IconButton>
        </div>

        <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => openDialog(null)}>
          Add Vendor
        </Button>
      </div>

      <section className="panel panel--flush">
        <div className="table-wrap">
          <table className="data-table">
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Vendor Name</th>
                <th>Vendor Type</th>
                <th>Contact Person</th>
                <th>Contact Number</th>
                <th>Email</th>
                <th>RFPs</th>
                <th className="cell-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="state-block">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : null}

              {!loading && error ? (
                <tr>
                  <td colSpan={7}>
                    <StateBlock
                      title="Could not load vendors"
                      description={errorMessage(error)}
                      action={
                        <Button variant="outlined" onClick={reload}>
                          Try again
                        </Button>
                      }
                    />
                  </td>
                </tr>
              ) : null}

              {!loading && !error && items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <StateBlock
                      title={
                        debouncedSearch ? `No vendors match "${debouncedSearch}".` : 'No vendors yet.'
                      }
                    />
                  </td>
                </tr>
              ) : null}

              {items.map((vendor) => {
                const typeMeta = VENDOR_TYPE_META[vendor.vendorType];
                return (
                  <tr key={vendor.id}>
                    <td>{vendor.name}</td>
                    <td>
                      <Badge variant={typeMeta.variant}>{typeMeta.label}</Badge>
                    </td>
                    <td>{vendor.contactPerson ?? '—'}</td>
                    <td>{vendor.contactNumber ?? '—'}</td>
                    <td>{vendor.email ?? '—'}</td>
                    <td>
                      <Metric
                        value={vendor.rfpCount}
                        hint={`${vendor.name} is invited to ${vendor.rfpCount} RFP(s)`}
                      />
                    </td>
                    <td className="cell-center">
                      <ActionMenu
                        label={`Actions for ${vendor.name}`}
                        items={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: <EditIcon size={16} />,
                            onSelect: () => openDialog(vendor),
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: <TrashIcon size={16} />,
                            danger: true,
                            separatorBefore: true,
                            onSelect: () => setDeleteTarget(vendor),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="table-footer">
        <Pagination
          page={pagination?.page ?? 1}
          totalPages={pagination?.totalPages ?? 1}
          onChange={setPage}
        />

        <div className="table-footer__per-page">
          <span className="table-footer__per-page-label">Per Page</span>
          <Select
            aria-label="Rows per page"
            options={PER_PAGE_OPTIONS}
            value={perPage}
            onChange={(event) => setPerPage(Number(event.target.value))}
            className="table-footer__select"
          />
        </div>

        <span className="table-footer__range">{pagination?.rangeLabel ?? '0-0 of 0'}</span>
      </div>

      {dialogOpen ? (
        <VendorFormDialog
          key={dialogKey}
          vendor={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={refresh}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete vendor"
        message={
          deleteTarget
            ? `${deleteTarget.name} will be removed. Vendors that are already invited to an RFP cannot be deleted - mark them inactive instead.`
            : ''
        }
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}
